import { useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useNavRailState } from "common-fe";
import {
  DAY_BAND_WIDTH,
  LIVE_DESK_WIDTH,
  LOOKUP_BAND_WIDTH,
  LOOKUP_OVERLAY_BREAKPOINT_PX,
} from "../constants/frame";

// AppBar/AppFooter's own fixed heights (common-fe AppLayout.jsx /
// MainContent.jsx / AppFooter.jsx all hardcode these same two numbers —
// none of the three exports them as a constant, so OrderOfGo.jsx's sticky
// header hardcodes them too and this frame follows the same precedent).
const APP_BAR_HEIGHT = 64;
const APP_FOOTER_HEIGHT = 56;

// #2138 (docs/score/announcer-ui-redesign.md §3) — the four-band layout
// frame. Band 1 (NavRail) is App.jsx's own AppLayout `navRail` slot
// (#2139); this component owns bands 2-4 only: the day (never scrolls),
// the live desk (never scrolls), and the look-up column (the ONLY band
// that ever scrolls). All three are content-agnostic here — this card
// ships the frame with every band empty (AC5); #2140/#2149/#2142 fill
// each one in via the dayBand/liveDeskBand/lookUpBand slots below.
//
// #2139 hit two separate stacking bugs putting the sticky header next to
// this same rail (OrderOfGo.jsx's own #2112/#2139 comments), and the same
// trap applies here: common-fe's MainContent wraps page content in a Box
// that only ever constrains `minHeight` and sets `overflow: "auto"` on
// itself, so normal-flow content taller than the viewport makes the
// WINDOW scroll, not the content. Bands 2 and 3 must never scroll at all
// (not "don't scroll until MainContent takes over"), so this frame needs
// a REAL fixed height of its own, not just a fixed header the way
// OrderOfGo's sticky header is. `top`/`left`/`right`/`bottom` are pinned
// to the viewport to match AppBar/AppFooter's own fixed dimensions, with
// `left` tracking `navRailWidth` (useNavRailState, the SAME shared value
// AppLayout itself reads) so this frame can never drift out of sync with
// the rail's expand/collapse the way #2139 had to fix twice.
function AnnouncerFrame({ statusBar = null, dayBand = null, liveDeskBand = null, lookUpBand = null }) {
  const { width: navRailWidth } = useNavRailState();
  // #2453 — this used to be `useMediaQuery` against raw window width, which
  // never accounted for the rail's own width: at a 1280px window with the
  // rail EXPANDED (200px) only ~1080px is actually available to the four
  // bands, but the window itself is still 1280px, so the media query never
  // tripped and the look-up column stayed in its wide layout and clipped
  // (card 2146's screenshot). This root is already sized to exactly the
  // space available to the bands (`left: navRailWidth`, `right: 0`), so
  // measuring ITS OWN rendered width (ResizeObserver, same idiom
  // LiveDesk.jsx's #2145/#2447 height ladder already uses) is correct
  // regardless of the rail's state, now or if the rail's own width ever
  // changes. Measured with `useLayoutEffect` and an immediate synchronous
  // `getBoundingClientRect()` read, not just the ResizeObserver's own
  // (async) first callback: this runs after the DOM commits but BEFORE the
  // browser paints, so the first paint already uses the correct width
  // instead of flashing the wide layout for one frame. That matters here
  // specifically because the overlay's own `transform: translateX(100%)`
  // carries a `transition: transform 0.2s ease` — a one-frame-late flip
  // would visibly animate in on every mount.
  const rootRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(null);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    setAvailableWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entriesList) => {
      const entry = entriesList[0];
      if (entry) setAvailableWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  // §3.6 — "below ~1100px" the look-up column overlays instead of sitting
  // beside the desk; 1100px itself is still the wide/inline layout
  // ("below", not "at or below"), hence the strict less-than.
  const narrow = availableWidth != null && availableWidth < LOOKUP_OVERLAY_BREAKPOINT_PX;

  return (
    <Box
      ref={rootRef}
      data-testid="frame-root"
      sx={{
        position: "fixed",
        top: APP_BAR_HEIGHT,
        left: navRailWidth,
        right: 0,
        bottom: APP_FOOTER_HEIGHT,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "background.default",
        transition: "left 0.15s ease",
      }}
    >
      {/* #2145 — StatusBar (#2144) re-homes here: it has no natural slot in
          the original 3-band contract (the redesign's own §3.1 wireframe
          draws the show name/clock inside the shared AppBar, but reworking
          that shared common-fe component is out of scope for a cutover
          card). It sits in NORMAL FLOW above the band row, not
          position:fixed the way OrderOfGo's old sticky header was — this
          root is already fixed to the viewport, so a normal-flow child
          here needs no ResizeObserver of its own to reserve space the way
          the old header did. `flexShrink: 0`: it must never be squeezed by
          the band row below it, matching every band's own contract. */}
      {statusBar ? (
        <Box
          data-testid="frame-status-bar"
          sx={{
            flexShrink: 0,
            overflow: "hidden",
            px: 3,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          {statusBar}
        </Box>
      ) : null}

      {/* #2145 — the band row, unchanged in every way except that it is now
          a column-flex child instead of the frame root itself (the root
          gained the status row above). `position: "relative"` is new and
          load-bearing: it is what lets the narrow-mode look-up overlay
          below anchor to "the space below the status bar" via
          `position: "absolute"` instead of hardcoding the status bar's
          height (which varies — StatusBar's own content can wrap, same as
          the old sticky header did at a small viewport, #1875). */}
      <Box
        data-testid="frame-bands-row"
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Band 2 — the day. §4.2: fixed 210px, never scrolls. Past its
            compress floor it may scroll, but only when the announcer drags
            it — that ladder is #2140's content logic, not this frame's; the
            frame's own job is just to never hand it a native scrollbar on
            its own, which `overflow: "hidden"` guarantees regardless of how
            tall #2140's real content ever gets. */}
        <Box
          data-testid="frame-band-day"
          sx={{
            width: DAY_BAND_WIDTH,
            flexShrink: 0,
            height: "100%",
            overflow: "hidden",
            borderRight: 1,
            borderColor: "divider",
          }}
        >
          {dayBand}
        </Box>

        {/* Band 3 — the live desk. §4.3: fixed 560px, NEVER scrolls, at any
            width or content length. */}
        <Box
          data-testid="frame-band-live-desk"
          sx={{
            width: LIVE_DESK_WIDTH,
            flexShrink: 0,
            height: "100%",
            overflow: "hidden",
            borderRight: narrow ? 0 : 1,
            borderColor: "divider",
          }}
        >
          {liveDeskBand}
        </Box>

        {/* Band 4 — the look-up column. §4.4/§3.6: the only band that ever
            scrolls, 398px wide, sitting beside the desk at >=1100px and
            overlaying it below that, closed by default. Nothing in this
            card ever opens it (a class click drives that, #2142's job) —
            `narrow` alone decides whether it renders inline or as a closed
            overlay; there is no open state to wire up yet.
            zIndex 1050 is deliberate, not left at `auto`: this box only
            ever needs to sit above the plain (non-positioned, z-index auto)
            day/live-desk boxes it overlays in narrow mode, so it sits BELOW
            every other fixed band in the app (AppFooter 1100, this frame's
            own navRail slot 1200 from AppLayout.jsx, AppBar 1300) — it never
            needs to contest any of those, and picking a number under all
            three keeps it that way on purpose rather than by accident.
            #2145 — `position: "absolute"` (was `"fixed"`), anchored to
            `frame-bands-row` (the new `position:"relative"` ancestor just
            above) instead of the viewport directly. A viewport-fixed overlay
            would have started right under the AppBar, on TOP of the newly
            re-homed status bar row — `frame-bands-row`'s own box already
            excludes that row (it's a sibling flex child, not an ancestor),
            so anchoring here reproduces the same rect as `frame-bands-row`
            itself (the band row, below the status row) rather than the old
            fixed rect, which began at the AppBar and so included the strip
            the status row now occupies — without needing to know the status
            row's own (variable, can-wrap) height. `getBoundingClientRect()`
            is unaffected by
            fixed-vs-absolute — it is always viewport-relative — so this does
            not change any existing bounding-rect assertion. */}
        <Box
          data-testid="frame-band-lookup"
          data-lookup-mode={narrow ? "overlay" : "inline"}
          data-lookup-open={narrow ? "false" : "true"}
          sx={{
            width: LOOKUP_BAND_WIDTH,
            flexShrink: 0,
            height: "100%",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderLeft: narrow ? 1 : 0,
            borderColor: "divider",
            ...(narrow && {
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              zIndex: 1050,
              boxShadow: 6,
              // Closed by default (§3.6, AC4): translated fully off the
              // right edge of the viewport. Still mounted (so its own
              // data-lookup-* attributes are inspectable) but not visible
              // and not overlapping the desk until something opens it.
              transform: "translateX(100%)",
              transition: "transform 0.2s ease",
            }),
          }}
        >
          {lookUpBand}
        </Box>
      </Box>
    </Box>
  );
}

export default AnnouncerFrame;
